import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Card, CardHeader } from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import { ColumnDef } from "@tanstack/react-table";
import { CodeCell, IndexColumn, SourceColumn } from "components/common/columns";
import { DateOnly, DateTime } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import MaterialRequestCancelConfirmModal from "features/MaterialRequests/components/MaterialRequestCancelConfirmModal";
import DeleteModal from "components/common/DeleteModal";
import { toast } from "react-toastify";
import { MaterialRequest } from "features/MaterialRequests/types/material-request.types";
import {
  cancelMaterialRequest,
  deleteMaterialRequest,
  getMaterialRequests,
} from "features/MaterialRequests/services/MaterialRequestService";
import StatusBadge from "components/common/StatusBadge";
import { MaterialRequestStatusMap } from "features/MaterialRequests/constants/material-request.constants";
import MaterialRequestApproveRejectModals from "features/MaterialRequests/components/MaterialRequestApproveRejectModals";
import MaterialRequestActionsDropdown from "features/MaterialRequests/components/MaterialRequestActionsDropdown";
import { invalidatePendingMaterialRequestCount } from "features/MaterialRequests/materialRequestPendingCountInvalidate";
import MaterialRequestListFilters from "./MaterialRequestListFilters";
import MaterialRequestNewModal from "../MaterialRequestNewModal";
import { getDocumentTitle } from "utils/helpers";
import { materialRequestDisplayCode } from "features/MaterialRequests/utils/material-request.utils";
import { materialRequestSourceColumnCell } from "features/MaterialRequests/utils/material-request-source-link";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

const MaterialRequestList = () => {
  document.title = getDocumentTitle("Material Requests");
  const { can } = useAccess();

  const tableRef = useRef<() => void>(null);
  const [materialRequest, setMaterialRequest] = useState<MaterialRequest>();
  const [approveModal, setApproveModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [sourceTypeFilter, setSourceTypeFilter] = useState<string>("");
  const [sourceIdFilter, setSourceIdFilter] = useState<number | "">("");

  const canRead = can(Permissions.MATERIAL_REQUEST.READ);
  const canCreate = can(Permissions.MATERIAL_REQUEST.CREATE);
  const canApprove = can(Permissions.MATERIAL_REQUEST.APPROVE);
  const canReject = can(Permissions.MATERIAL_REQUEST.REJECT);
  const canUpdate = can(Permissions.MATERIAL_REQUEST.UPDATE);
  const canDelete = can(Permissions.MATERIAL_REQUEST.DELETE);

  const [newModal, setNewModal] = useState(false);

  const requestParams = useMemo(
    () => ({
      status: statusFilter || undefined,
      sourceType: sourceTypeFilter || undefined,
      sourceId: sourceIdFilter === "" ? undefined : Number(sourceIdFilter),
    }),
    [sourceIdFilter, sourceTypeFilter, statusFilter],
  );

  const onClickApprove = useCallback((record: MaterialRequest) => {
    setMaterialRequest(record);
    setApproveModal(true);
  }, []);

  const onClickReject = useCallback((record: MaterialRequest) => {
    setMaterialRequest(record);
    setRejectModal(true);
  }, []);

  const onClickCancel = useCallback((record: MaterialRequest) => {
    setMaterialRequest(record);
    setCancelModal(true);
  }, []);

  const onClickDelete = useCallback((record: MaterialRequest) => {
    setMaterialRequest(record);
    setDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const id = materialRequest?.id;
    if (id == null) return;
    try {
      const res = await deleteMaterialRequest(id);
      if (res.success) {
        toast.success(res.message ?? "Material request deleted.");
        invalidatePendingMaterialRequestCount();
        setDeleteModal(false);
        tableRef.current?.();
      } else {
        toast.error(res.message ?? "Delete failed.");
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Delete failed.",
      );
    }
  }, [materialRequest?.id]);

  const handleConfirmCancel = useCallback(async () => {
    const id = materialRequest?.id;
    if (id == null) return;
    setCancelSubmitting(true);
    try {
      const res = await cancelMaterialRequest(id);
      if (res.success) {
        toast.success(res.message ?? "Material request cancelled.");
        invalidatePendingMaterialRequestCount();
        setCancelModal(false);
        tableRef.current?.();
      } else {
        toast.error(res.message ?? "Cancel failed.");
      }
    } catch (err) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Cancel failed.",
      );
    } finally {
      setCancelSubmitting(false);
    }
  }, [materialRequest?.id]);

  const columns: ColumnDef<MaterialRequest>[] = useMemo(
    () => [
      IndexColumn<MaterialRequest>(),
      {
        header: "Code",
        cell: ({ row }) => {
          const label = materialRequestDisplayCode(row.original);
          if (!canRead) return <CodeCell code={label} />;
          return (
            <Link
              to={`/inventory-management/material-requests/${row.original.id}`}
              className="text-decoration-none link"
            >
              <CodeCell code={label} />
            </Link>
          );
        },
        id: "code",
        enableColumnFilter: false,
      },
      SourceColumn<MaterialRequest>({
        id: "source",
        resolve: materialRequestSourceColumnCell,
        accessorFn: (row: MaterialRequest) =>
          [
            row.sourceType,
            row.workOrder?.code,
            row.pettyWork?.code,
            row.sourceId,
          ]
            .filter(Boolean)
            .join(" "),
      }),

      {
        header: "Total amount",
        id: "totalAmount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const v = row.original.totalAmount;
          return v != null && Number.isFinite(Number(v)) ? (
            <span className="tabular-nums">{formatCurrency(Number(v))}</span>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Required date",
        accessorKey: "requiredDate",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const d = row.original.requiredDate;
          return d ? (
            <DateOnly date={d} />
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge
            value={row.original.status}
            map={MaterialRequestStatusMap}
          />
        ),
        accessorKey: "status",
        enableColumnFilter: false,
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => <DateTime date={row.original.created_at} />,
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <MaterialRequestActionsDropdown
            record={row.original}
            canRead={canRead}
            canApprove={canApprove}
            canReject={canReject}
            canUpdate={canUpdate}
            canDelete={canDelete}
            onApprove={onClickApprove}
            onReject={onClickReject}
            onCancel={onClickCancel}
            onDelete={onClickDelete}
          />
        ),
      },
    ],
    [
      canRead,
      canApprove,
      canReject,
      canUpdate,
      canDelete,
      onClickApprove,
      onClickReject,
      onClickCancel,
      onClickDelete,
    ],
  );

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => void handleConfirmDelete()}
        onCloseClick={() => setDeleteModal(false)}
      />
      <MaterialRequestNewModal
        isOpen={newModal}
        onClose={() => setNewModal(false)}
      />
      <MaterialRequestApproveRejectModals
        record={materialRequest}
        approveOpen={approveModal}
        rejectOpen={rejectModal}
        onCloseApprove={() => setApproveModal(false)}
        onCloseReject={() => setRejectModal(false)}
        onSuccess={() => {
          tableRef.current?.();
        }}
      />
      <MaterialRequestCancelConfirmModal
        show={cancelModal}
        submitting={cancelSubmitting}
        onClose={() => setCancelModal(false)}
        onConfirm={handleConfirmCancel}
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Material Requests"
            pageTitle="Inventory Management"
          />
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Material requests</h5>
              {canCreate ? (
                <button
                  type="button"
                  className="btn btn-primary "
                  onClick={() => setNewModal(true)}
                >
                  <i className="ri-add-line align-bottom me-1" />
                  Add material request
                </button>
              ) : null}
            </CardHeader>
            <div className="card-body pt-0">
              <TableContainer
                columns={columns}
                fetchApi={getMaterialRequests}
                requestParams={requestParams}
                pageSize={10}
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
                toolbarRight={
                  <MaterialRequestListFilters
                    statusFilter={statusFilter}
                    sourceTypeFilter={sourceTypeFilter}
                    sourceIdFilter={sourceIdFilter}
                    onStatusChange={setStatusFilter}
                    onSourceTypeChange={setSourceTypeFilter}
                    onSourceIdChange={setSourceIdFilter}
                  />
                }
              />
            </div>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default MaterialRequestList;
